import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { formatApiError } from "../../../lib/api";
import { GAME_PHASES, resolveInitialPhase } from "../../core/phases.js";
import { decline as declineAction } from "../actions/decline.js";
import { loadState } from "../actions/loadState.js";
import { optIn as optInAction } from "../actions/optIn.js";
import { skip as skipAction } from "../actions/skip.js";
import { submitVotes as submitVotesAction } from "../actions/submitVotes.js";

function isAbortError(error) {
  return axios.isCancel(error) || error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
}

export function useFavoriteDancesSession({
  enabled = true,
  eventId,
  occurrenceDate,
  dancerId,
  dataSource = "api",
  demoActions = null,
  initialDemoState = null,
  onError,
  onSubmitted,
}) {
  const [loading, setLoading] = useState(Boolean(enabled));
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState(initialDemoState);
  const [phase, setPhase] = useState(() => resolveInitialPhase(initialDemoState));
  const [selected, setSelected] = useState(() =>
    Array.isArray(initialDemoState?.my_votee_ids)
      ? initialDemoState.my_votee_ids
      : Array.isArray(initialDemoState?.votee_ids)
        ? initialDemoState.votee_ids
        : [],
  );
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (dataSource === "demo") {
      setLoading(false);
      setLoadError(null);
      if (initialDemoState) {
        setState(initialDemoState);
        setSelected(
          Array.isArray(initialDemoState.votee_ids) ? initialDemoState.votee_ids : [],
        );
        setPhase(resolveInitialPhase(initialDemoState));
      }
      return;
    }
    if (!eventId || !dancerId) return;

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setLoadError(null);

    loadState(eventId, { dancerId, occurrenceDate, signal: controller.signal })
      .then((data) => {
        if (cancelled) return;
        setState(data);
        setSelected(Array.isArray(data.my_votee_ids) ? data.my_votee_ids : []);
        setPhase(resolveInitialPhase(data));
      })
      .catch((error) => {
        if (cancelled || isAbortError(error)) return;
        const message = formatApiError(error);
        setLoadError(message);
        onError?.(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, eventId, occurrenceDate, dancerId, dataSource, reloadKey, initialDemoState, onError]);

  const refreshState = useCallback(async () => {
    if (dataSource === "demo" && demoActions?.getState) {
      const next = demoActions.getState();
      setState(next);
      return next;
    }
    const data = await loadState(eventId, { dancerId, occurrenceDate });
    setState(data);
    return data;
  }, [dataSource, demoActions, eventId, dancerId, occurrenceDate]);

  const optIn = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (dataSource === "demo" && demoActions?.optIn) {
        const next = demoActions.optIn();
        setState(next);
      } else {
        await optInAction(eventId, { dancerId, occurrenceDate });
        setState((prev) => ({ ...prev, opted_in: true }));
      }
      setPhase(GAME_PHASES.PLAY);
    } finally {
      setBusy(false);
    }
  }, [busy, dataSource, demoActions, eventId, dancerId, occurrenceDate]);

  const decline = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (dataSource === "demo" && demoActions?.decline) {
        const next = demoActions.decline();
        setState(next);
      } else {
        await declineAction(eventId, { dancerId, occurrenceDate });
      }
      setPhase(GAME_PHASES.DECLINED);
    } finally {
      setBusy(false);
    }
  }, [busy, dataSource, demoActions, eventId, dancerId, occurrenceDate]);

  const submit = useCallback(async () => {
    if (busy || selected.length === 0) return null;
    setBusy(true);
    try {
      if (dataSource === "demo" && demoActions?.submit) {
        const next = demoActions.submit(selected);
        setState(next);
        setPhase(GAME_PHASES.RESULTS);
        onSubmitted?.(next);
        return next;
      }
      await submitVotesAction(eventId, { dancerId, occurrenceDate, voteeIds: selected });
      const data = await refreshState();
      setPhase(GAME_PHASES.RESULTS);
      onSubmitted?.(data);
      return data;
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    selected,
    dataSource,
    demoActions,
    eventId,
    dancerId,
    occurrenceDate,
    refreshState,
    onSubmitted,
  ]);

  const dismiss = useCallback(async () => {
    if (busy) return;
    if (
      dataSource === "api"
      && phase === GAME_PHASES.PLAY
      && state?.opted_in === true
      && !state?.submitted
    ) {
      try {
        await skipAction(eventId, { dancerId, occurrenceDate });
      } catch {
        /* best-effort */
      }
    }
  }, [busy, dataSource, phase, state, eventId, dancerId, occurrenceDate]);

  const togglePick = useCallback((id) => {
    const maxPicks = state?.max_picks || 3;
    setSelected((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      if (current.length >= maxPicks) return current;
      return [...current, id];
    });
  }, [state?.max_picks]);

  const syncDemoState = useCallback((next) => {
    setState(next);
    setPhase(resolveInitialPhase(next));
    if (Array.isArray(next?.votee_ids)) setSelected(next.votee_ids);
  }, []);

  return {
    loading,
    loadError,
    state,
    phase,
    setPhase,
    selected,
    busy,
    reload,
    optIn,
    decline,
    submit,
    dismiss,
    togglePick,
    syncDemoState,
  };
}
